import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Loader2, Trash2, Pencil, FileDown, ShoppingCart, Copy, Mail, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import CustomerSearchSelect from "@/components/CustomerSearchSelect";
import { CreatorBadge } from "@/components/CreatorBadge";
import QuoteItemRow from "@/components/quotes/QuoteItemRow";
import { computeItem } from "@/components/quotes/quoteUtils";
import { rupee, uid } from "@/lib/format";

const blankItem = () => ({ uid: uid(), inventory_id: "", item_name: "", hsn: "", description: "", qty: 1, unit_price: "", gst_inclusive: false });
const blank = { customer_name: "", mobile: "", email: "", customer_gst: "", customer_id: "", notes: "", items: [blankItem()] };

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [nextNo, setNextNo] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [convertId, setConvertId] = useState(null);
  const [converting, setConverting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/quotes", { params });
      setItems(data);
      setSelectedIds(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(fetchItems, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openNew = async () => {
    setForm(blank);
    setEditingId(null);
    setOpen(true);
    try { const { data } = await api.get("/quotes/next-no"); setNextNo(data.quote_no); }
    catch (e) { setNextNo(""); }
  };

  const emailQuote = async (qq) => {
    const to = qq.email || window.prompt("Send quotation to which email address?", "");
    if (!to) return;
    try { const { data } = await api.post(`/quotes/${qq.id}/email`, { to }); toast.success(`Quotation emailed to ${data.to}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (path, filename) => {
    const token = localStorage.getItem("access_token");
    try {
      const r = await fetch(`${API}/${path}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) throw new Error("fail");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const sendWhatsApp = async (qq) => {
    try {
      const { data } = await api.post(`/quotes/${qq.id}/share`);
      const url = `${API}/quotes/public/${data.token}`;
      const num = (qq.mobile || "").replace(/\D/g, "");
      const wa = num.length === 10 ? `91${num}` : num;
      const msg = `Please find the attached quotation for your reference.\n${url}`;
      const link = wa ? `https://wa.me/${wa}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(link, "_blank", "noopener");
      toast.success("Opening WhatsApp… link valid for 7 days");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (qq) => {
    setEditingId(qq.id);
    setNextNo(qq.quote_no);
    setForm({
      customer_name: qq.customer_name || "",
      mobile: qq.mobile || "",
      email: qq.email || "",
      customer_gst: qq.customer_gst || "",
      customer_id: qq.customer_id || "",
      notes: qq.notes || "",
      items: (qq.items || []).map((it) => ({
        uid: uid(),
        inventory_id: it.inventory_id || "",
        item_name: it.item_name || "",
        hsn: it.hsn || "",
        description: it.description || "",
        qty: it.qty ?? 1,
        unit_price: it.unit_price ?? "",
        gst_inclusive: !!it.gst_inclusive,
      })),
    });
    setOpen(true);
  };

  const openDuplicate = async (qq) => {
    setEditingId(null);
    setForm({
      customer_name: qq.customer_name || "",
      mobile: qq.mobile || "",
      email: qq.email || "",
      customer_gst: qq.customer_gst || "",
      customer_id: qq.customer_id || "",
      notes: qq.notes || "",
      items: (qq.items || []).map((it) => ({
        uid: uid(),
        inventory_id: "",
        item_name: it.item_name || "",
        hsn: it.hsn || "",
        description: it.description || "",
        qty: it.qty ?? 1,
        unit_price: it.unit_price ?? "",
        gst_inclusive: !!it.gst_inclusive,
      })),
    });
    setOpen(true);
    try { const { data } = await api.get("/quotes/next-no"); setNextNo(data.quote_no); }
    catch (e) { setNextNo(""); }
  };

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeRow = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const totals = form.items.reduce((acc, it) => {
    const c = computeItem(it);
    return { taxable: acc.taxable + c.taxable, gst: acc.gst + c.gst, total: acc.total + c.line_total };
  }, { taxable: 0, gst: 0, total: 0 });

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    const valid = form.items.filter((it) => (it.item_name || "").trim() || (it.description || "").trim());
    if (valid.length === 0) { toast.error("Add at least one item with a name or description"); return; }
    setSaving(true);
    const payload = {
      customer_name: form.customer_name, mobile: form.mobile, email: form.email,
      customer_gst: form.customer_gst, customer_id: form.customer_id, notes: form.notes,
      items: valid.map((it) => ({
        inventory_id: it.inventory_id || "", item_name: it.item_name || "", hsn: it.hsn || "", description: it.description || "",
        qty: Number(it.qty || 0), unit_price: Number(it.unit_price || 0),
        gst_inclusive: !!it.gst_inclusive,
      })),
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/quotes/${editingId}`, payload);
        setItems((prev) => prev.map((x) => (x.id === editingId ? data : x)));
        setOpen(false);
        toast.success(`Quotation ${data.quote_no} updated`);
      } else {
        const { data } = await api.post("/quotes", payload);
        setItems((prev) => [data, ...prev]);
        setOpen(false);
        toast.success(`Quotation ${data.quote_no} created`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    const id = deleteId;
    const prev = items;
    setItems((p) => p.filter((x) => x.id !== id));
    setDeleteId(null);
    try { await api.delete(`/quotes/${id}`); toast.success("Quotation deleted"); }
    catch (e) { setItems(prev); toast.error(formatApiError(e)); }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(items.map((x) => x.id)));

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const prev = items;
    setItems((p) => p.filter((x) => !selectedIds.has(x.id)));
    setBulkConfirm(false);
    try {
      const { data } = await api.post("/quotes/bulk-delete", { ids });
      setSelectedIds(new Set());
      toast.success(`${data.deleted} quotation(s) deleted`);
    } catch (e) { setItems(prev); toast.error(formatApiError(e)); }
  };

  const confirmConvert = async () => {
    const id = convertId;
    setConverting(true);
    try {
      const { data } = await api.post(`/quotes/${id}/convert`);
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, converted: true } : x)));
      setConvertId(null);
      toast.success(`Converted to sale — ${data.count} device(s) marked Sold`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConverting(false); }
  };

  const hasStockItem = (qq) => (qq.items || []).some((i) => (i.inventory_id || "").trim());

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Module 04</div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Quotations</h1>
          <p className="text-sm text-[#475569] mt-1">Build GST quotations from stock or manual items, then download an A4 PDF.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="add-quote-btn">
          <Plus className="w-4 h-4" /> New quotation
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input placeholder="Search by quote no, customer, mobile…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="quotes-search" />
        </div>
        {selectedIds.size > 0 && (
          <Button variant="outline" onClick={() => setBulkConfirm(true)} className="h-10 gap-2 text-[#EF4444] border-[#EF4444]/40 hover:bg-red-50" data-testid="bulk-delete-quotes-btn">
            <Trash2 className="w-4 h-4" /> Delete ({selectedIds.size})
          </Button>
        )}
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-[#F8F9FA]">
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} aria-label="Select all" data-testid="quotes-select-all" />
              </TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Quote No</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Customer</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-center">Items</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Grand Total</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Date</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Created By</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-[#64748B]">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-[#64748B]" data-testid="quotes-empty">
                No quotations yet.
              </TableCell></TableRow>
            ) : items.map((qq) => (
              <TableRow key={qq.id} data-testid={`quote-row-${qq.quote_no}`} data-state={selectedIds.has(qq.id) ? "selected" : undefined}>
                <TableCell>
                  <Checkbox checked={selectedIds.has(qq.id)} onCheckedChange={() => toggleSelect(qq.id)} aria-label="Select row" data-testid={`quote-select-${qq.quote_no}`} />
                </TableCell>
                <TableCell className="font-mono text-sm font-medium">
                  {qq.quote_no}
                  {qq.converted && (
                    <span className="ml-2 inline-block align-middle text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534] border border-[#22C55E]/30" data-testid={`quote-converted-${qq.quote_no}`}>Converted</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm">{qq.customer_name}</div>
                  <div className="font-mono text-[11px] text-[#64748B]">{qq.mobile}</div>
                </TableCell>
                <TableCell className="text-center text-sm">{(qq.items || []).length}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{rupee(qq.grand_total)}</TableCell>
                <TableCell className="font-mono text-xs text-[#64748B]">{(qq.created_at || "").slice(0, 10)}</TableCell>
                <TableCell><CreatorBadge name={qq.created_by} testId={`quote-creator-${qq.quote_no}`} /></TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    {!qq.converted && hasStockItem(qq) && (
                      <Button variant="ghost" size="sm" onClick={() => setConvertId(qq.id)} title="Convert to sale (stock items)" data-testid={`convert-quote-${qq.quote_no}`}>
                        <ShoppingCart className="w-4 h-4 text-[#16A34A]" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(qq)} title="Edit quotation" data-testid={`edit-quote-${qq.quote_no}`}>
                      <Pencil className="w-4 h-4 text-[#0055FF]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openDuplicate(qq)} title="Duplicate quotation" data-testid={`duplicate-quote-${qq.quote_no}`}>
                      <Copy className="w-4 h-4 text-[#7C3AED]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf(`quotes/${qq.id}/pdf`, `${(qq.quote_no || "quotation").replace(/\//g, "-")}.pdf`)} title="Download PDF (A4)" data-testid={`download-quote-${qq.quote_no}`}>
                      <FileDown className="w-4 h-4 text-[#0055FF]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => sendWhatsApp(qq)} title="Send via WhatsApp Web" data-testid={`whatsapp-quote-${qq.quote_no}`}>
                      <MessageCircle className="w-4 h-4 text-[#16A34A]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => emailQuote(qq)} title="Email to customer" data-testid={`email-quote-${qq.quote_no}`}>
                      <Mail className="w-4 h-4 text-[#0F766E]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(qq.id)} data-testid={`delete-quote-${qq.quote_no}`}>
                      <Trash2 className="w-4 h-4 text-[#EF4444]" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] xl:max-w-[1400px] h-[94vh] flex flex-col overflow-hidden" data-testid="quote-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-display tracking-tight">{editingId ? "Edit quotation" : "New quotation"}</DialogTitle>
            <DialogDescription>
              Quote No: <span className="font-mono text-[#0055FF]">{nextNo || "…"}</span> · GST auto-calculated @ 18%
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,7fr)] gap-5 items-stretch flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
            {/* LEFT ~30% — customer details & totals */}
            <div className="space-y-3 lg:pr-5 lg:border-r lg:border-[#E2E8F0] lg:h-full lg:overflow-y-auto">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Existing customer (optional)</Label>
                <CustomerSearchSelect
                  selectedId={form.customer_id || null}
                  onPick={(c) => setForm((f) => c
                    ? ({ ...f, customer_id: c.id, customer_name: c.name, mobile: c.mobile, email: c.email || "", customer_gst: c.gstin || "" })
                    : ({ ...f, customer_id: "", customer_name: "", mobile: "", email: "", customer_gst: "" }))}
                  placeholder="Search customers by name or mobile…"
                  testId="quote-customer-picker"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Customer Name *</Label>
                  <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="h-8" data-testid="quote-customer-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Mobile</Label>
                  <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="h-8 font-mono" data-testid="quote-mobile" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Customer GSTIN</Label>
                  <Input value={form.customer_gst} onChange={(e) => setForm({ ...form, customer_gst: e.target.value.toUpperCase() })} className="h-8 font-mono" placeholder="GSTIN" maxLength={15} data-testid="quote-customer-gst" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-8" data-testid="quote-email" />
                </div>
              </div>

              <div className="space-y-1.5 text-sm border border-[#E2E8F0] rounded-md p-3 bg-white">
                <div className="flex justify-between"><span className="text-[#64748B]">Subtotal (taxable)</span><span className="font-mono" data-testid="quote-subtotal">{rupee(totals.taxable)}</span></div>
                <div className="flex justify-between"><span className="text-[#64748B]">GST @ 18%</span><span className="font-mono" data-testid="quote-gst">{rupee(totals.gst)}</span></div>
                <div className="flex justify-between pt-1.5 border-t border-[#E2E8F0] font-semibold text-base"><span>Grand Total</span><span className="font-mono" data-testid="quote-grand-total">{rupee(totals.total)}</span></div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Notes (optional)</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="min-h-[56px] text-sm" data-testid="quote-notes" />
              </div>
            </div>

            {/* RIGHT ~70% — line items */}
            <div className="flex flex-col space-y-2 min-w-0 lg:h-full lg:min-h-0">
              <div className="flex items-center justify-between shrink-0">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Line items</Label>
                  <p className="text-[11px] text-[#94A3B8]">Toggle "Incl" if a rate already includes GST.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addRow} className="h-8 gap-1.5 text-xs" data-testid="quote-add-item">
                  <Plus className="w-3.5 h-3.5" /> Add item
                </Button>
              </div>
              <div className="space-y-2 overflow-x-auto flex-1 lg:overflow-y-auto lg:min-h-0 pr-1">
                {form.items.map((it, idx) => (
                  <QuoteItemRow
                    key={it.uid || idx}
                    it={it}
                    idx={idx}
                    canRemove={form.items.length > 1}
                    onChange={(patch) => setItem(idx, patch)}
                    onRemove={() => removeRow(idx)}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2 shrink-0 border-t border-[#E2E8F0] mt-0">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="quote-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white" data-testid="quote-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? "Save changes" : "Create quotation")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete quotation?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-quote-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} quotation(s)?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-bulk-quote-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!convertId} onOpenChange={(o) => !o && setConvertId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert quotation to sale?</AlertDialogTitle>
            <AlertDialogDescription>
              Stock items in this quotation will be sold at their line total (incl. GST), marked <strong>Sold</strong>, and the customer details prefilled. The quotation will be marked <strong>Converted</strong>. Deleting the resulting sale later will revert this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmConvert} disabled={converting} className="bg-[#16A34A] hover:bg-[#15803D]" data-testid="confirm-quote-convert">
              {converting ? "Converting…" : "Convert to sale"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
